import { TextButton } from '@creditornot/cb-button';
import { MagnifyingGlass } from '@creditornot/cb-icons';
import { cbSemanticCssVars, Typography as T } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { usePluralization } from 'common/hooks';

import { MenuCatalogListData } from '../types';

type Props = {
  listData: MenuCatalogListData[];
  searchQuery: string;
  selectedItemIds: string[];
  allowDuplicateGtins?: boolean;
  onChangeSearchQuery: (searchQuery: string) => void;
  onSelectItemIds: (gtins: string[]) => void;
};

const Toolbar = styled.div`
  padding: 16px 24px;
  display: flex;
  align-items: center;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const SelectedAmount = styled(T.Small)`
  margin-inline-start: 16px;
  color: ${cbSemanticCssVars.textSecondary};
`;

const SearchInput = styled(Input)`
  margin-inline-start: auto;
`;

const SelectAndSearch = ({
  listData,
  searchQuery,
  selectedItemIds,
  allowDuplicateGtins,
  onChangeSearchQuery,
  onSelectItemIds,
}: Props) => {
  const intl = useIntl();
  const { FormattedPluralMessage } = usePluralization();

  const selectableItemIds = useMemo(() => {
    if (allowDuplicateGtins) {
      return listData.filter(item => !!item.gtin).map(item => item.itemId);
    }
    const dedupedListData = listData.reduce<MenuCatalogListData[]>(
      (acc, curr) => (!curr.gtin || acc.find(item => item.gtin === curr.gtin) ? acc : [...acc, curr]),
      [],
    );
    return dedupedListData.map(item => item.itemId);
  }, [allowDuplicateGtins, listData]);

  const areAllGtinsSelected = useMemo(
    () => selectableItemIds.length !== 0 && selectableItemIds.sort().join(',') === selectedItemIds.sort().join(','),
    [selectableItemIds, selectedItemIds],
  );

  return (
    <Toolbar>
      {!areAllGtinsSelected && (
        <TextButton
          variant="blue"
          onClick={() => onSelectItemIds(selectableItemIds)}
          disabled={selectableItemIds.length === 0}
        >
          <FormattedMessage id="menu.catalog.select-all" />
        </TextButton>
      )}
      {areAllGtinsSelected && (
        <TextButton variant="red" onClick={() => onSelectItemIds([])}>
          <FormattedMessage id="menu.catalog.deselect-all" />
        </TextButton>
      )}
      {selectedItemIds.length > 0 && (
        <SelectedAmount>
          <FormattedPluralMessage id="menu.catalog.items-selected" count={selectedItemIds.length} />
        </SelectedAmount>
      )}
      <SearchInput
        icon={<MagnifyingGlass />}
        placeholder={intl.formatMessage({ id: 'menu.catalog.search-placeholder' })}
        stretch={false}
        value={searchQuery}
        onChange={e => onChangeSearchQuery(e.target.value)}
      />
    </Toolbar>
  );
};

export default SelectAndSearch;
